import type { Terminal as XTerm } from '@xterm/xterm';
import type { InteractivePtyDataEvent } from '../types/command-executor.types';
import { getTerminalShortcutSequence } from './terminal-shortcuts';

export type AgentPtyXterm = Pick<
  XTerm,
  'write' | 'onData' | 'attachCustomKeyEventHandler' | 'scrollToBottom'
>;

const SESSION_ENDED_BANNER = '\r\n\x1b[2m[session ended]\x1b[0m\r\n';
const SESSION_NOT_FOUND_BANNER =
  '\x1b[2m[session is no longer running]\x1b[0m\r\n';

export function unseenPtyText({
  event,
  seenLength,
}: {
  event: Pick<InteractivePtyDataEvent, 'text' | 'offset'>;
  seenLength: number;
}): string {
  const end = event.offset + event.text.length;

  if (end <= seenLength) {
    return '';
  }

  return event.text.slice(Math.max(0, seenLength - event.offset));
}

export async function connectAgentPtyTerminal({
  xterm,
  ptyId,
  signal,
}: {
  xterm: AgentPtyXterm;
  ptyId: string;
  signal: AbortSignal;
}): Promise<() => void> {
  const api = window.electronAPI.pty;
  const pending: InteractivePtyDataEvent[] = [];
  let seenLength: number | null = null;
  let endedBanner: string | null = null;

  const writeEvent = (event: InteractivePtyDataEvent) => {
    if (seenLength === null) {
      pending.push(event);

      return;
    }

    const text = unseenPtyText({ event, seenLength });

    if (text) {
      xterm.write(text);
      seenLength = event.offset + event.text.length;
    }
  };

  const markEnded = (banner: string) => {
    if (endedBanner !== null) {
      return;
    }

    endedBanner = banner;

    if (seenLength !== null) {
      xterm.write(banner);
    }
  };

  const showEndedAfterAttach = (fallbackBanner: string | null) => {
    if (endedBanner !== null) {
      xterm.write(endedBanner);
    } else if (fallbackBanner !== null) {
      markEnded(fallbackBanner);
    }
  };

  const unsubscribeData = api.onData((event) => {
    if (event.ptyId === ptyId) {
      writeEvent(event);
    }
  });
  const unsubscribeExit = api.onExit((event) => {
    if (event.ptyId === ptyId) {
      markEnded(SESSION_ENDED_BANNER);
    }
  });
  const sendInput = (data: string) => {
    if (endedBanner !== null) {
      return;
    }

    api.input({ ptyId, data }).catch((error: unknown) => {
      console.warn(
        '[AgentPtyTerminal] Failed to forward input; session closed',
        {
          error: error instanceof Error ? error : new Error(String(error)),
          props: { ptyId },
        },
      );
      markEnded(SESSION_ENDED_BANNER);
    });
  };

  const disposeInput = xterm.onData(sendInput);

  xterm.attachCustomKeyEventHandler((event) => {
    if (event.type !== 'keydown') {
      return true;
    }

    const shortcutSequence = getTerminalShortcutSequence(event);

    if (shortcutSequence) {
      sendInput(shortcutSequence);

      return false;
    }

    return true;
  });

  const cleanup = () => {
    unsubscribeData();
    unsubscribeExit();
    disposeInput.dispose();
  };

  try {
    const screen = await api.read({ ptyId });

    if (signal.aborted) {
      cleanup();

      return () => undefined;
    }

    xterm.write('\x1bc');
    xterm.write(screen.rawTail, () => xterm.scrollToBottom());
    seenLength = screen.outputLength;
    pending.splice(0).forEach(writeEvent);

    showEndedAfterAttach(screen.alive ? null : SESSION_ENDED_BANNER);
  } catch (error: unknown) {
    console.warn('[AgentPtyTerminal] Session not found; showing it as ended', {
      error: error instanceof Error ? error : new Error(String(error)),
      props: { ptyId },
    });
    seenLength = Number.POSITIVE_INFINITY;
    showEndedAfterAttach(SESSION_NOT_FOUND_BANNER);
  }

  return cleanup;
}
